import React, { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  Stack,
  Snackbar,
  Alert,
} from '@mui/material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useCreateUserProg, useDeleteUserProg, useGetUserProg } from './Services';
import { useGetProgetti } from '../gestversore/Services';

interface Progetto {
  codprog: string;
  desprog: string;
}

interface UserProg {
  id: number;
  codprog: string;
  username: string;
}

interface Props {
  username: string;
  open: boolean;
  onClose: () => void;
  onSave: () => void;
}

const DropMonitorProgetti: React.FC<Props> = ({ username, open, onClose, onSave }) => {
  const { data: userProgs, isLoading: loadingUserProgs } = useGetUserProg(username);
  const { data: allProgettiData, isLoading: loadingAllProgetti } = useGetProgetti(2);

  const [saveMessageOpen, setSaveMessageOpen] = useState(false);
  const allProgetti = allProgettiData?.results || [];

  const [selectedProgetti, setSelectedProgetti] = useState<Progetto[]>([]);
  const [availableProgetti, setAvailableProgetti] = useState<Progetto[]>([]);
  const [originalUserProgs, setOriginalUserProgs] = useState<UserProg[]>([]);

  const [availableSearch, setAvailableSearch] = useState('');
  const [selectedSearch, setSelectedSearch] = useState('');

  const createUserProgMutation = useCreateUserProg();
  const deleteUserProgMutation = useDeleteUserProg();

  useEffect(() => {
    if (!loadingUserProgs && !loadingAllProgetti && userProgs && allProgetti.length) {
      const selectedCodes = new Set(userProgs.map(p => p.codprog?.trim()));

      const cleanedProgetti = allProgetti
        .filter(p => p.codprog)
        .map(p => ({
          codprog: p.codprog!.trim(),
          desprog: p.desprog ?? '',
        }));

      const selected = cleanedProgetti
        .filter(p => selectedCodes.has(p.codprog))
        .sort((a, b) => a.desprog.localeCompare(b.desprog));

      const available = cleanedProgetti
        .filter(p => !selectedCodes.has(p.codprog))
        .sort((a, b) => a.desprog.localeCompare(b.desprog));

      setSelectedProgetti(selected);
      setAvailableProgetti(available);
      setOriginalUserProgs(userProgs);
    }
  }, [loadingUserProgs, loadingAllProgetti, userProgs, allProgetti]);

  const onDragEnd = (result: any) => {
    const { source, destination } = result;
    if (!destination) return;

    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    let sourceList = source.droppableId === 'available' ? [...availableProgetti] : [...selectedProgetti];
    let destList = destination.droppableId === 'available' ? [...availableProgetti] : [...selectedProgetti];

    const [moved] = sourceList.splice(source.index, 1);
    destList.splice(destination.index, 0, moved);

    if (source.droppableId === 'available') setAvailableProgetti(sourceList);
    else setSelectedProgetti(sourceList);

    if (destination.droppableId === 'available') setAvailableProgetti(destList);
    else setSelectedProgetti(destList);
  };

  const handleSave = async () => {
    try {
      const selectedCodes = new Set(selectedProgetti.map(p => p.codprog));
      const originalCodes = new Set(originalUserProgs.map(p => p.codprog));

      const toAdd = selectedProgetti.filter(p => !originalCodes.has(p.codprog));
      const toRemove = originalUserProgs.filter(p => !selectedCodes.has(p.codprog));

      await Promise.all(
        toAdd.map(p =>
          createUserProgMutation.mutateAsync({
            id: 0,
            username,
            codprog: p.codprog,
          })
        )
      );

      await Promise.all(
        toRemove.map(p =>
          deleteUserProgMutation.mutateAsync({ id: p.id })
        )
      );

      setSaveMessageOpen(true);
      onSave();
    } catch (error) {
      console.error(error);
    }
  };

  const filteredAvailable = availableProgetti.filter(p =>
    p.desprog.toLowerCase().includes(availableSearch.toLowerCase())
  );

  const filteredSelected = selectedProgetti.filter(p =>
    p.desprog.toLowerCase().includes(selectedSearch.toLowerCase())
  );

  if (loadingUserProgs || loadingAllProgetti) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Seleziona Progetti per {username}</DialogTitle>
      <DialogContent>
        <DragDropContext onDragEnd={onDragEnd}>
          <Stack direction="row" spacing={3} justifyContent="center" sx={{ mt: 2 }}>
            <Droppable droppableId="available">
              {(provided) => (
                <Box
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{ width: 400, height: 600, overflowY: 'auto', border: '1px solid gray', fontSize: '0.7rem', borderRadius: 1, p: 1 }}
                >
                  <Typography variant="h6" mb={1}>Progetti Inclusi Monitoraggio</Typography>
                  <input
                    type="text"
                    placeholder="Cerca..."
                    value={availableSearch}
                    onChange={(e) => setAvailableSearch(e.target.value)}
                    style={{ width: '100%', marginBottom: '8px', padding: '4px' }}
                  />
                  {filteredAvailable.map((proj, index) => (
                    <Draggable key={proj.codprog} draggableId={proj.codprog} index={index}>
                      {(provided) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          sx={{ p: 1, mb: 1, bgcolor: 'lightgray', borderRadius: 1, cursor: 'grab' }}
                        >
                          {proj.desprog}
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>

            <Droppable droppableId="selected">
              {(provided) => (
                <Box
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{ width: 400, height: 600, overflowY: 'auto', border: '1px solid #1976d2', fontSize: '0.7rem', borderRadius: 1, p: 1 }}
                >
                  <Typography variant="h6" mb={1}>Progetti Esclusi Monitoraggio</Typography>
                  <input
                    type="text"
                    placeholder="Cerca..."
                    value={selectedSearch}
                    onChange={(e) => setSelectedSearch(e.target.value)}
                    style={{ width: '100%', marginBottom: '8px', padding: '4px' }}
                  />
                  {filteredSelected.map((proj, index) => (
                    <Draggable key={proj.codprog} draggableId={proj.codprog} index={index}>
                      {(provided) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          sx={{ p: 1, mb: 1, bgcolor: '#90caf9', borderRadius: 1, cursor: 'grab' }}
                        >
                          {proj.desprog}
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </Stack>
        </DragDropContext>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>Chiudi</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={createUserProgMutation.isPending || deleteUserProgMutation.isPending}
        >
          {(createUserProgMutation.isPending || deleteUserProgMutation.isPending) ? <CircularProgress size={24} /> : 'Salva'}
        </Button>
      </DialogActions>

      <Snackbar
        open={saveMessageOpen}
        autoHideDuration={3000}
        onClose={() => setSaveMessageOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ mb: 5 }}
      >
        <Alert onClose={() => setSaveMessageOpen(false)} severity="success" sx={{ width: '100%' }}>
          Salvataggio completato con successo!
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default DropMonitorProgetti;
